// /var/www/oszczedzam/client/src/components/goals/GoalForm.js
import React, { useState } from 'react';
import { 
  Box, Button, TextField, FormControl, InputLabel, 
  Select, MenuItem, Grid, Paper, Typography, Slider,
  InputAdornment, FormHelperText 
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { pl } from 'date-fns/locale';

function GoalForm({ onSubmit, initialData, isEditing = false }) {
  // Stan formularza
  const [name, setName] = useState(initialData?.name || '');
  const [targetAmount, setTargetAmount] = useState(initialData?.targetAmount || '');
  const [currentAmount, setCurrentAmount] = useState(initialData?.currentAmount || '0');
  const [targetDate, setTargetDate] = useState(initialData?.targetDate ? new Date(initialData.targetDate) : null);
  const [category, setCategory] = useState(initialData?.category || 'Inne');
  const [priority, setPriority] = useState(initialData?.priority || 'medium');
  const [description, setDescription] = useState(initialData?.description || '');
  const [errors, setErrors] = useState({});

  // Kategorie celów oszczędnościowych
  const goalCategories = [
    'Podróże', 'Mieszkanie', 'Samochód', 'Edukacja', 'Elektronika', 
    'Emerytura', 'Zabezpieczenie finansowe', 'Inwestycje', 'Inne'
  ];

  // Priorytety
  const priorities = [
    { value: 'low', label: 'Niski' },
    { value: 'medium', label: 'Średni' },
    { value: 'high', label: 'Wysoki' }
  ];

  // Walidacja formularza
  const validateForm = () => {
    const newErrors = {};
    
    if (!name.trim()) newErrors.name = 'Nazwa celu jest wymagana';
    if (!targetAmount || isNaN(targetAmount) || parseFloat(targetAmount) <= 0) {
      newErrors.targetAmount = 'Docelowa kwota musi być większa od zera';
    }
    if (currentAmount && (isNaN(currentAmount) || parseFloat(currentAmount) < 0)) {
      newErrors.currentAmount = 'Aktualna kwota nie może być ujemna';
    }
    if (targetDate && new Date(targetDate) < new Date()) {
      newErrors.targetDate = 'Data docelowa nie może być w przeszłości';
    }
    if (parseFloat(currentAmount) > parseFloat(targetAmount)) {
      newErrors.currentAmount = 'Aktualna kwota nie może być większa od docelowej';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Obsługa zatwierdzania formularza
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    // Przygotuj dane celu
    const goalData = {
      name,
      targetAmount: parseFloat(targetAmount),
      currentAmount: parseFloat(currentAmount) || 0,
      targetDate: targetDate ? targetDate.toISOString().split('T')[0] : null,
      category,
      priority,
      description,
      progress: currentAmount ? (parseFloat(currentAmount) / parseFloat(targetAmount)) * 100 : 0
    };
    
    // Wywołaj funkcję przekazaną przez props
    onSubmit(goalData);
  };

  // Oblicz czas do osiągnięcia celu
  const calculateTimeToGoal = () => {
    if (!targetDate) return 'Brak daty docelowej';
    
    const today = new Date();
    const target = new Date(targetDate);
    const diffTime = Math.abs(target - today);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return `${diffDays} dni`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} ${months === 1 ? 'miesiąc' : months < 5 ? 'miesiące' : 'miesięcy'}`;
    } else {
      const years = Math.floor(diffDays / 365);
      const months = Math.floor((diffDays % 365) / 30);
      return `${years} ${years === 1 ? 'rok' : years < 5 ? 'lata' : 'lat'}${months > 0 ? ` i ${months} ${months === 1 ? 'miesiąc' : months < 5 ? 'miesiące' : 'miesięcy'}` : ''}`;
    }
  };

  // Oblicz sugerowaną miesięczną kwotę oszczędności
  const calculateSuggestedMonthlySaving = () => {
    if (!targetDate || !targetAmount || parseFloat(targetAmount) <= 0) return 0;
    
    const today = new Date();
    const target = new Date(targetDate);
    const diffTime = Math.abs(target - today);
    const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
    
    if (diffMonths <= 0) return 0;
    
    const remaining = parseFloat(targetAmount) - parseFloat(currentAmount || 0);
    return (remaining / diffMonths).toFixed(2);
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {isEditing ? 'Edytuj cel oszczędnościowy' : 'Nowy cel oszczędnościowy'}
      </Typography>
      
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          {/* Nazwa celu */}
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              id="name"
              label="Nazwa celu"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>
          
          {/* Docelowa kwota */}
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="targetAmount"
              label="Docelowa kwota"
              type="number"
              InputProps={{ 
                inputProps: { min: 0, step: 0.01 },
                endAdornment: <InputAdornment position="end">PLN</InputAdornment>
              }}
              value={targetAmount}
              onChange={(e) => setTargetAmount(e.target.value)}
              error={!!errors.targetAmount}
              helperText={errors.targetAmount}
            />
          </Grid>
          
          {/* Aktualna kwota */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="currentAmount"
              label="Aktualna kwota oszczędności"
              type="number"
              InputProps={{ 
                inputProps: { min: 0, step: 0.01 },
                endAdornment: <InputAdornment position="end">PLN</InputAdornment>
              }}
              value={currentAmount}
              onChange={(e) => setCurrentAmount(e.target.value)}
              error={!!errors.currentAmount}
              helperText={errors.currentAmount}
            />
          </Grid>
          
          {/* Data docelowa */}
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
              <DatePicker
                label="Data docelowa"
                value={targetDate}
                onChange={(newDate) => setTargetDate(newDate)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.targetDate,
                    helperText: errors.targetDate
                  }
                }}
                minDate={new Date()}
              />
            </LocalizationProvider>
          </Grid>
          
          {/* Kategoria */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="category-label">Kategoria</InputLabel>
              <Select
                labelId="category-label"
                id="category"
                value={category}
                label="Kategoria"
                onChange={(e) => setCategory(e.target.value)}
              >
                {goalCategories.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Priorytet */}
          <Grid item xs={12}>
            <Typography id="priority-slider" gutterBottom>
              Priorytet
            </Typography>
            <FormControl fullWidth>
              <Select
                id="priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                {priorities.map((p) => (
                  <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Opis */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="description"
              label="Opis celu (opcjonalnie)"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          
          {/* Sugestie oszczędzania */}
          {targetDate && targetAmount && parseFloat(targetAmount) > 0 && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Sugestie oszczędzania:
                </Typography>
                <Typography variant="body2">
                  • Czas do osiągnięcia celu: <strong>{calculateTimeToGoal()}</strong>
                </Typography>
                <Typography variant="body2">
                  • Sugerowana miesięczna kwota oszczędności: <strong>{calculateSuggestedMonthlySaving()} PLN</strong>
                </Typography>
              </Paper>
            </Grid>
          )}
          
          {/* Przyciski */}
          <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" type="submit">
              {isEditing ? 'Zapisz zmiany' : 'Dodaj cel'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}

export default GoalForm;